import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type components } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_USER, UPDATE_AUTO_EXTRACT_INTENT_MUTATION_KEY } from '../../contracts/keys';
import { type UserResponse } from '../../contracts/types/user-types';
import { updateAutoExtractIntent } from '../api/user';
import { USER_SETTINGS_TOASTS } from './userSettingsToasts.messages';

export { UPDATE_AUTO_EXTRACT_INTENT_MUTATION_KEY };

type UpdateAutoExtractIntentRequest =
  components['schemas']['UpdateAutoExtractIntentRequest'];

export const useMutateUpdateAutoExtractIntent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_AUTO_EXTRACT_INTENT_MUTATION_KEY],
    mutationFn: async (input: UpdateAutoExtractIntentRequest) => {
      await updateAutoExtractIntent(input);
    },
    meta: USER_SETTINGS_TOASTS.updateAutoExtractIntent,
    onMutate: async ({ intends_to_auto_extract_patient_context }) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser?.session_settings) {
        queryClient.setQueryData<UserResponse>([GET_USER], {
          ...previousUser,
          session_settings: {
            ...previousUser.session_settings,
            intends_to_auto_extract_patient_context,
          },
        });
      }

      return { previousUser };
    },
    onError: (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error('Failed to update auto-extract intent', sanitizeError(error));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
  });
};
